/**
 * When the patient/provider is already viewing the same chat thread, skip
 * global toast + sound for chat Socket alerts — the chat UI already receives
 * `message:new` / thread updates (avoids duplicate alerts).
 *
 * Prefer the in-memory active conversation id (set by chat hubs) over the
 * ephemeral `?openConversation=` query, which PatientChatsHub clears on select.
 */

import { getActiveChatConversationId } from "./activeChatConversation";

function norm(s) {
  return String(s ?? "").trim();
}

/**
 * @param {string} type notification:new payload.type
 * @param {Record<string, unknown>} payload
 * @param {'patient'|'doctor'|'hospital'|null} role
 * @param {{ pathname?: string, search?: string, state?: Record<string, unknown> }} location react-router location
 */
export function shouldSuppressChatSocketToast(type, payload, role, location) {
  const t = norm(type).toLowerCase();
  if (t !== "chat_message" && t !== "chat_reply") return false;

  const path = norm(location?.pathname);
  /** Care-price “composer” (nav with no explicit MR): bid-thread system previews feel like auto-bids. */
  if (role === "patient" && path.includes("/care-price")) {
    const explicitMr = norm(location?.state?.matchRequestId);
    if (!explicitMr) {
      const body = String(payload?.body ?? "").toLowerCase();
      const bidish =
        /bid update|reverse bid|counter offer|offer accepted|offer declined|provider accepted|provider declined|treatment bill|visit outcome|appointment (confirmed|update|completed)/i.test(
          body
        );
      if (bidish) return true;
    }
  }

  const convId = norm(payload?.conversationId);
  if (!convId) return false;

  // Primary: shared store set while a thread is open.
  if (getActiveChatConversationId() === convId) return true;

  const rawSearch = location?.search;
  const search =
    typeof rawSearch === "string" && rawSearch.startsWith("?")
      ? rawSearch.slice(1)
      : norm(rawSearch);
  const qs = new URLSearchParams(search);

  if (role === "patient" && path.includes("/patient-chats")) {
    return norm(qs.get("openConversation")) === convId;
  }
  if (role === "doctor" && path.includes("/doctor-chats")) {
    return norm(qs.get("openConversation")) === convId;
  }
  if (role === "hospital" && path.includes("/hospital-chats")) {
    return norm(qs.get("openConversation")) === convId;
  }

  return false;
}
